/**
 * Resource dependency hints.
 *
 * When the "Include Dependencies" toggle is on, exporting a selected resource
 * also pulls in the resources it references (queues need skills, flows need
 * queues, and so on). We cannot know the exact instance count client-side —
 * that is discovered by the exporter — but we DO know, from the Genesys Cloud
 * data model, which *kinds* of resources a given type typically depends on.
 *
 * This module maps a selector resource type (the internal `type` string used
 * in `RESOURCE_TYPE_CATEGORIES`, e.g. `'Routing Queue'`) to the friendly names
 * of the dependency resource types that are commonly auto-added, so the
 * selector can show an honest "will also pull in…" preview without over-
 * promising a precise number.
 */

/**
 * Map of selector resource type -> friendly dependency resource-type names.
 * Only types with meaningful, well-understood dependencies are listed; a type
 * absent from this map is treated as having no auto-added dependencies to
 * surface (which is honest — we do not claim dependencies we cannot justify).
 */
const DEPENDENCY_HINTS: Readonly<Record<string, readonly string[]>> = {
    'Routing Queue': ['ACD Skills', 'Wrap-up Codes', 'Operating Schedules', 'Divisions', 'Flows'],
    'Architect Flow': ['Queues', 'ACD Skills', 'User Prompts', 'Data Tables', 'Grammars'],
    'Architect IVR': ['Flows', 'Operating Schedules'],
    'Architect Schedule Groups': ['Operating Schedules'],
    'Architect Emergency Group': ['Flows'],
    'Outbound Campaign': ['Contact Lists', 'Call Analysis Response Sets', 'DNC Lists', 'Queues', 'Rulesets'],
    'Outbound Messaging Campaign': ['Contact Lists', 'DNC Lists', 'Digital Rulesets'],
    'Outbound Sequence': ['Outbound Campaigns'],
    'Outbound Contact List Filter': ['Contact Lists'],
    'Routing Skill Group': ['ACD Skills', 'Divisions'],
    'Group Roles': ['Groups', 'Roles', 'Divisions'],
    'Station': ['Phones'],
    'User': ['Roles', 'Groups', 'ACD Skills', 'Divisions'],
} as const;

/**
 * Returns the friendly dependency resource-type names that are commonly
 * auto-added when the given selector resource type is exported with
 * "Include Dependencies" enabled. Returns an empty array when the type has no
 * dependencies we can confidently surface.
 *
 * @param resourceType Internal selector type string (e.g. `'Routing Queue'`).
 */
export function getDependencyHint(resourceType: string): readonly string[] {
    return DEPENDENCY_HINTS[resourceType] ?? [];
}

/**
 * Returns true when the given selector resource type has dependency kinds we
 * can surface — useful for gating a hint badge without allocating an array.
 */
export function hasDependencyHint(resourceType: string): boolean {
    return (DEPENDENCY_HINTS[resourceType]?.length ?? 0) > 0;
}

/**
 * Builds a short, human-readable sentence describing the dependency kinds that
 * will be auto-added for the given resource type, or `null` when there are
 * none to surface. Example: "Also pulls in ACD Skills, Wrap-up Codes and 3
 * more dependency types."
 *
 * @param resourceType Internal selector type string.
 */
export function formatDependencyHint(resourceType: string): string | null {
    const deps = getDependencyHint(resourceType);
    if (deps.length === 0) return null;

    const shown = deps.slice(0, 3);
    const remaining = deps.length - shown.length;

    if (remaining > 0) {
        return `Also pulls in ${shown.join(', ')} and ${remaining} more dependency type${remaining > 1 ? 's' : ''}.`;
    }

    const list =
        shown.length === 1
            ? shown[0]
            : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;

    return `Also pulls in ${list}.`;
}
